import React, { useEffect } from 'react';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import CustomButton from 'components/CustomInput/Button';
import CustomTable from 'components/CustomTable';
import useSitePIData from 'hooks/tableData/useSitePIData';
import TipsIcon from 'components/TipsIcon';
import MemberDialog from './MemberDialog';
import useDialog from 'components/ConfirmDialog/useDialog';
import {
    AT_LEAST_ONE_SITE_PI,
    CO_EXIST_HOSPITAL,
    EMAIL_EXIST_EMAIL,
    HOSPITAL_EXIST_IS_SITE_MEMBER,
    SELECT_WORKFLOW_FIRST,
    SITE_REMOVED,
    SI_CREATE_CONFIRM,
    SI_DELETE_CONFIRM,
    SI_DELETE_REMINDER,
    SI_EDIT_CONFIRM,
} from 'utility/frontendMessage';
import { compareEmail, produce } from 'utility/general';
import { UPDATE_REMOTE_APPLICATION_CUSTOM_DATA } from 'store/key';
import { useDispatch } from 'react-redux';
import { getStore } from 'store';
import { setGlobalLoading } from 'store/global/reducer';
import useSubscribe from 'hooks/useSubscribe';

const SiteInfoTable = ({
    onTableChange = (data) => null,
    defaultData = [],
    workflow = '',
    isFormCreate = false,
    ciDelegateData = [],
    disableOnPermission = false,
    onTableDataUpdate = (data) => null,
    applicationDetail,
    applicationCreateDtm,
    isAmendmentCreate = false,
    isAmendmentEdit = false,
    isHighlight = () => {},
}) => {
    const {
        openPopup,
        setOpenPopup,
        columns,
        rows,
        triggerFormCreate,
        handleEditClick,
        selectedRow,
        onRecordUpdate,
        setRows,
        isEdit,
    } = useSitePIData({
        formId: applicationDetail?.applicationForm?.id,
    });

    const showDialog = useDialog();
    const dispatch = useDispatch();

    const isAmendment = isAmendmentEdit || isAmendmentCreate;

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        const { applicationDetailPostData } = getStore().getState();
        const pi = applicationDetailPostData.postData?.pi ?? [];
        setRows([...rows, ...pi]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        onTableDataUpdate(rows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    useSubscribe('clearPiData', () => {
        setRows([]);
    });

    const checkEmailExistInForm = (data, isUpdate = false) => {
        let isExist = false;

        // ciDelegateData.forEach((item) => {
        //     if (item.email === data.email) {
        //         isExist = true;
        //     }
        // });

        rows.filter((item) => {
            if (isUpdate) {
                return item.id !== data.id;
            }

            return true;
        }).forEach((item) => {
            if (compareEmail(item.email, data.email)) {
                isExist = true;
            }
        });

        return isExist;
    };

    const onTeamMemberFormDataUpdateChecking = async (data, isUpdate = false) => {
        let isEmailExistInTeamMember = checkEmailExistInForm(data, isUpdate);
        let isHospitalExist = false;

        rows.filter((item) => {
            if (isUpdate) {
                return item.id !== data.id;
            }

            return true;
        }).forEach((item) => {
            if (item.institution === data.institution) {
                isHospitalExist = true;
            }
        });

        if (isEmailExistInTeamMember) {
            await showDialog({
                description: EMAIL_EXIST_EMAIL,
                disableCancelButton: true,
            });

            return false;
        }

        if (isHospitalExist) {
            await showDialog({
                description: HOSPITAL_EXIST_IS_SITE_MEMBER,
                disableCancelButton: true,
            });

            return false;
        }

        return true;
    };

    const onDataCreate = async (data, { isDelegateDialog }, callback = () => null) => {
        // check if user is already exist in the team member form
        const isSuccess = await onTeamMemberFormDataUpdateChecking(data);

        if (isSuccess) {
            if (isFormCreate) {
                // check not co-exist hospital in site-pi only
                if (!isDelegateDialog) {
                    const isHospitalExist = rows.some((item) => item.institution === data.institution);

                    if (isHospitalExist) {
                        await showDialog({
                            description: CO_EXIST_HOSPITAL,
                            disableCancelButton: true,
                        });
                        throw new Error('hospital exist');
                    }
                }

                onRecordUpdate(data);
                setOpenPopup(false);
                onTableChange([...rows, data]);
                callback();
            } else {
                if (isAmendment) {
                    setGlobalLoading(true);
                    dispatch({
                        type: UPDATE_REMOTE_APPLICATION_CUSTOM_DATA,
                        payload: {
                            id: applicationDetail.applicationForm.id,
                            data: {
                                pi: [data],
                            },
                            actionType: 'study_info',
                            callback: () => {
                                showDialog({
                                    description: SI_CREATE_CONFIRM,
                                    disableCancelButton: true,
                                });
                                callback();
                                setOpenPopup(false);
                            },
                            final: () => {
                                setGlobalLoading(false);
                            },
                        },
                    });
                    // handleCreateAfterCreation(data);
                } else {
                    delete data.id;
                    setGlobalLoading(true);

                    dispatch({
                        type: UPDATE_REMOTE_APPLICATION_CUSTOM_DATA,
                        payload: {
                            id: applicationDetail.applicationForm.id,
                            data: {
                                pi: [data],
                            },
                            actionType: 'study_info',
                            callback: () => {
                                showDialog({
                                    description: SI_CREATE_CONFIRM,
                                    disableCancelButton: true,
                                });
                                callback();
                                setOpenPopup(false);
                            },
                            final: () => {
                                setGlobalLoading(false);
                            },
                        },
                    });
                }
            }
        }
    };

    const onEditData = async (data) => {
        const isSuccess = await onTeamMemberFormDataUpdateChecking(data, true);

        if (isSuccess) {
            if (isFormCreate) {
                const newRows = produce(rows, (draft) => {
                    const index = draft.findIndex((item) => {
                        return item.id === data.id;
                    });

                    draft[index] = data;
                });

                onTableChange(newRows);
                setOpenPopup(false);
                setRows(newRows);
            } else {
                setGlobalLoading(true);

                dispatch({
                    type: UPDATE_REMOTE_APPLICATION_CUSTOM_DATA,
                    payload: {
                        id: applicationDetail.applicationForm.id,
                        data: {
                            pi: [data],
                            siteInfo: [
                                {
                                    id: data.siteInfoId,
                                    institution: data.institution,
                                },
                            ],
                        },
                        actionType: 'study_info',
                        callback: () => {
                            showDialog({
                                description: SI_EDIT_CONFIRM,
                                disableCancelButton: true,
                            });
                            // callback();
                            setOpenPopup(false);
                        },
                        final: () => {
                            setGlobalLoading(false);
                        },
                    },
                });

                setOpenPopup(false);
            }
        }
    };

    const onDeleteData = async (data) => {
        if (rows.length === 1) {
            return showDialog({ description: AT_LEAST_ONE_SITE_PI, disableCancelButton: true });
        }

        // case of new team member form
        if (isFormCreate) {
            const newRows = produce(rows, (draft) => {
                const index = draft.findIndex((item) => item.id === data.id);
                draft.splice(index, 1);
            });

            onTableChange(newRows);
            setOpenPopup(false);
            setRows(newRows);
        } else {
            // case of updating team member when form has created

            if (isAmendment) {
                const isConfirm = await showDialog({
                    description: SI_DELETE_REMINDER,
                });

                if (isConfirm) {
                    // handleDeleteAfterCreation(data);
                    setGlobalLoading(true);

                    dispatch({
                        type: UPDATE_REMOTE_APPLICATION_CUSTOM_DATA,
                        payload: {
                            callback: () => {
                                showDialog({
                                    title: SITE_REMOVED,
                                    disableCancelButton: true,
                                });
                            },
                            final: () => {
                                setGlobalLoading(false);
                            },
                            id: applicationDetail.applicationForm.id,
                            siteInfoId: data.siteInfo.id,
                            actionType: 'study_info',
                            data: {
                                deleteRequest: {
                                    pi: [
                                        {
                                            id: data.id,
                                        },
                                    ],
                                },
                            },
                        },
                    });
                }
            } else {
                const isConfirm = await showDialog({
                    description: SI_DELETE_CONFIRM,
                });

                if (isConfirm) {
                    setGlobalLoading(true);

                    dispatch({
                        type: UPDATE_REMOTE_APPLICATION_CUSTOM_DATA,
                        payload: {
                            callback: () => {
                                showDialog({
                                    title: SITE_REMOVED,
                                    disableCancelButton: true,
                                });
                            },
                            final: () => {
                                setGlobalLoading(false);
                            },
                            id: applicationDetail.applicationForm.id,
                            siteInfoId: data.siteInfo.id,
                            actionType: 'study_info',
                            data: {
                                deleteRequest: {
                                    pi: [
                                        {
                                            id: data.id,
                                        },
                                    ],
                                },
                            },
                        },
                    });
                }
            }
        }
    };

    return (
        <>
            <div style={{ paddingBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextSubHeader>
                    <span style={{ background: isHighlight(['pi'], true) ? 'yellow' : 'initial' }}>
                        Information of Site PI(s)
                    </span>{' '}
                    <TipsIcon
                        messages={[
                            '- If Coordinating Investigator is also the Site PI, please fill in his/her information here as well.',
                            '- To take responsibility for the conduct of study at his/her study site',
                            '- Accept Site PI from cluster-based or hospital-based',
                            `- To fill in 'Site Info' of the Application Form and to provide site-specific application documents (e.g. CV, declaration form etc.) and relevant signature pages`,
                            `- To submit site-specific Post-approval Applications (e.g. Progress Report, Local SAE Report, Protocol Deviation / Compliance Incident Report, Site-specific Amendment etc.)`,
                        ]}
                    />
                </TextSubHeader>
                <CustomButton
                    disableOnPermission={disableOnPermission}
                    onClick={() => {
                        if (!workflow) {
                            showDialog({
                                description: SELECT_WORKFLOW_FIRST,
                                disableCancelButton: true,
                            });
                        } else {
                            triggerFormCreate(true);
                        }
                    }}
                >
                    Add
                </CustomButton>
            </div>

            {openPopup && (
                <MemberDialog
                    title={`Information of Site PI(s)`}
                    onDialogCancel={() => setOpenPopup(false)}
                    disablePosition
                    workflow={workflow}
                    restrictApplicant
                    disableHKU={workflow === 'N'}
                    disableCUHK={workflow === 'H'}
                    disableClusterChange={isAmendment && isEdit}
                    disableInstitutionChange={isAmendment && isEdit}
                    selectedRow={selectedRow}
                    onCreateSubmit={onDataCreate}
                    onEditSubmit={onEditData}
                    isEdit={isEdit}
                />
            )}

            <CustomTable
                fullWidthTable
                disableOnPermission={disableOnPermission}
                columns={columns}
                rows={rows}
                isDeleteAllow
                isEditAllow
                disableDeleteConfirm
                disableOldDataDelete={isAmendment}
                applicationCreateDtm={applicationCreateDtm}
                onDelete={onDeleteData}
                onEdit={handleEditClick}
            />
        </>
    );
};

export default SiteInfoTable;
